
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { setupAuthStateListener, signOutUser } from "../lib/firebase";
import api from "../api/axios";
import type { FirebaseUser } from "../types";
import type { MeResponse } from "../api/types";

type AuthState = {
  isAuthenticated: boolean;
  firebaseUser: FirebaseUser | null;
  isLoading: boolean;
};

type AuthContextValue = AuthState & {
  clearAuth: () => void;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function normalizePath(path: string): string {
  return path.replace(/\/$/, "");
}

function isOtpPage(): boolean {
  return normalizePath(window.location.pathname) === "/otp";
}

function readReturnUrl(): string | null {
  return new URLSearchParams(window.location.search).get("returnUrl");
}

function safeDefaultReturnUrl(): string {
  return "https://joymember.app";
}

function redirectToReturnUrl() {
  const returnUrl = readReturnUrl() || safeDefaultReturnUrl();
  window.location.assign(returnUrl);
}

async function fetchMe(): Promise<MeResponse> {
  const res = await api.get<MeResponse>("/api/members/whoiam");
  return res.data;
}

function isUnauthorized(err: any): boolean {
  const status = err?.response?.status;
  return status === 401 || status === 403;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
                                                                        children,
                                                                      }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    firebaseUser: null,
    isLoading: true,
  });

  const mountedRef = useRef(false);
  const verifyingRef = useRef(false);

  const clearAuth = useCallback(() => {
    delete api.defaults.headers.common.Authorization;
    setState({ isAuthenticated: false, firebaseUser: null, isLoading: false });
  }, []);

  const signOut = useCallback(async () => {
    try {
      await signOutUser();
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  const setFirebaseUserOnly = useCallback((fbUser: any) => {
    setState(prev => ({
      ...prev,
      isLoading: false,
      firebaseUser: {
        uid: fbUser.uid,
        email: fbUser.email || null,
        phoneNumber: fbUser.phoneNumber || null,
      },
    }));
  }, []);

  const setAuthenticated = useCallback((fbUser: any) => {
    setState({
      isAuthenticated: true,
      isLoading: false,
      firebaseUser: {
        uid: fbUser.uid,
        email: fbUser.email || null,
        phoneNumber: fbUser.phoneNumber || null,
      },
    });
  }, []);

  const verifyCookieSessionAndRedirect = useCallback(async () => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;

    try {
      await fetchMe();
      redirectToReturnUrl();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }
      // eslint-disable-next-line no-console
      console.error("[AuthProvider] /whoiam failed:", err);
      setState(prev => ({ ...prev, isLoading: false }));
    } finally {
      verifyingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    let unsub: (() => void) | null = null;

    (async () => {
      try {
        unsub = await setupAuthStateListener(async fbUser => {
          if (!fbUser) {
            clearAuth();
            return;
          }

          if (isOtpPage()) {
            setFirebaseUserOnly(fbUser);
            return;
          }

          // At this point Firebase user exists, but member session is cookie-based.
          // OTP page should call /exchange which sets cookies.
          // Here we only check if cookies are already valid (SSO), then redirect.
          setAuthenticated(fbUser);
          await verifyCookieSessionAndRedirect();
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[AuthProvider] Failed to attach listener", e);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    })();

    return () => {
      if (unsub) unsub();
    };
  }, [clearAuth, setAuthenticated, setFirebaseUserOnly, verifyCookieSessionAndRedirect]);

  const value = useMemo<AuthContextValue>(
      () => ({
        ...state,
        clearAuth,
        signOut,
      }),
      [state, clearAuth, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
};
